import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteConfig } from '@/mocks/site';
import { testimonials } from '@/mocks/articles';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt')?.toString().trim() || '';
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9spatl8o1vn02ekujig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查您的连接');
      setFormStatus('error');
    }
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    email: siteConfig.contactInfo.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '建国路88号SOHO现代城A座',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteConfig.name}`,
    description: siteConfig.description,
    url: `${siteConfig.url}/about`,
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description={`了解${siteConfig.name}的品牌故事、核心价值和专业团队。致力于帮助每一个家庭建立和谐温暖的关系。`}
        keywords="关于我们,年轻的岳母,品牌故事,家庭关系,联系我们"
        canonicalPath="/about"
        schema={[organizationSchema, aboutSchema]}
      />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />

            <div className="mt-6 max-w-3xl">
              <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
                关于 {siteConfig.name}
              </h1>
              <p className="text-sm text-foreground-600 mb-8">
                用专业的知识和温暖的态度，帮助每一个家庭找到属于自己的相处之道
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
              <div>
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-4">
                  品牌故事
                </h2>
                <div
                  className="text-sm text-foreground-700 leading-relaxed space-y-4"
                  dangerouslySetInnerHTML={{ __html: siteConfig.brandStory }}
                />
              </div>
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=warm%20cozy%20modern%20office%20space%20with%20team%20of%20people%20working%20collaboratively%20soft%20natural%20light%20streaming%20through%20large%20windows%20plants%20and%20books%20minimalist%20interior%20cream%20and%20beige%20tones%20editorial%20photography%20professional%20but%20warm%20atmosphere&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="品牌故事"
                  className="w-full h-72 md:h-96 object-cover rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-bold font-heading text-foreground-950 text-center mb-8">
              核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
              {siteConfig.coreValues.map((value) => (
                <div key={value.title} className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-heart-line text-xl text-accent-600"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{value.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-bold font-heading text-foreground-950 text-center mb-8">
              核心团队
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {siteConfig.teamMembers.map((member) => (
                <div key={member.name} className="bg-background-50 border border-background-200/70 rounded-lg p-6 flex gap-4">
                  <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-bold text-primary-600">{member.avatar}</span>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-950">{member.name}</h3>
                    <p className="text-xs text-primary-600 font-medium mb-2">{member.role}</p>
                    <p className="text-sm text-foreground-600 leading-relaxed">{member.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-bold font-heading text-foreground-950 text-center mb-8">
              读者心声
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t) => (
                <div key={t.name} className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6">
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4 italic">
                    "{t.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center">
                      <span className="text-sm font-semibold text-secondary-700">{t.avatar}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold font-heading text-foreground-950">
                联系我们
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                有任何问题或建议？欢迎随时联系我们
              </p>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                id="contact-form"
                className="space-y-5"
              >
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    您的姓名
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    电子邮箱
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入您的电子邮箱"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                    placeholder="请输入您想说的话（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                </div>

                <div className="hp-field">
                  <label htmlFor="phone_alt">备用电话</label>
                  <input
                    type="text"
                    id="phone_alt"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-3 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  发送留言
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-accent-600 text-center font-medium">
                    感谢您的留言！我们会尽快与您联系。
                  </p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-500 text-center">
                    {formError || '提交失败，请稍后再试'}
                  </p>
                )}
              </form>
            </div>

            <div className="mt-8 text-center text-sm text-foreground-500 space-y-1">
              <p>邮箱：{siteConfig.contactInfo.email}</p>
              <p>微信：{siteConfig.contactInfo.wechat}</p>
              <p>地址：{siteConfig.contactInfo.address}</p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}